import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Bot, Send, Sparkles, BookOpen, HelpCircle, ListChecks, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

const AIAssistantPage = ({ onMenuClick }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const quickActions = [
    {
      id: 'resume',
      label: 'Résumer mon cours',
      prompt: 'Peux-tu me faire un résumé clair et structuré du cours que je suis en train d\'étudier ?',
      icon: BookOpen,
      color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 hover:bg-blue-200 dark:hover:bg-blue-900/30'
    },
    {
      id: 'explain',
      label: 'Expliquer ce chapitre',
      prompt: 'Peux-tu m\'expliquer en détail les concepts clés de ce chapitre avec des exemples concrets ?',
      icon: HelpCircle,
      color: 'bg-emerald-100 dark:bg-emerald-900/20 text-emerald-600 hover:bg-emerald-200 dark:hover:bg-emerald-900/30'
    },
    {
      id: 'questions',
      label: 'Générer des questions de révision',
      prompt: 'Génère-moi 10 questions de révision pour tester ma compréhension du cours, avec des questions variées (QCM, vrai/faux, questions ouvertes).',
      icon: ListChecks,
      color: 'bg-amber-100 dark:bg-amber-900/20 text-amber-600 hover:bg-amber-200 dark:hover:bg-amber-900/30'
    },
    {
      id: 'exam',
      label: 'Préparer mon examen',
      prompt: 'Aide-moi à préparer mon prochain examen. Quels sont les points essentiels à retenir et comment puis-je organiser mes révisions efficacement ?',
      icon: GraduationCap,
      color: 'bg-violet-100 dark:bg-violet-900/20 text-violet-600 hover:bg-violet-200 dark:hover:bg-violet-900/30'
    }
  ];

  useEffect(() => {
    setMessages([
      {
        role: 'assistant',
        content: "Bonjour ! Je suis votre assistant pédagogique IA. Je suis là pour vous aider avec vos cours, répondre à vos questions et vous accompagner dans votre apprentissage. Comment puis-je vous aider aujourd'hui ?"
      }
    ]);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (messageToSend = null) => {
    const userMessage = messageToSend || input.trim();
    if (!userMessage || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post('/ai/chat', {
        message: userMessage,
        session_id: sessionId,
        course_id: null
      });

      setMessages((prev) => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: "Désolé, une erreur s'est produite. Veuillez réessayer." }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    handleSend(action.prompt);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Assistant IA" onMenuClick={onMenuClick} />
      
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">Assistant Pédagogique IA</h2>
          </div>
          <p className="text-slate-600 dark:text-slate-400">Posez vos questions sur vos cours et votre apprentissage</p>
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Actions rapides</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action)}
                  disabled={loading}
                  className={`${action.color} p-4 rounded-xl transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`quick-action-${action.id}`}
                >
                  <Icon className="w-5 h-5 mb-2 group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-medium">{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Card className="h-[calc(100vh-420px)] min-h-[400px] flex flex-col" data-testid="ai-chat-card">
          <CardHeader className="border-b border-slate-200 dark:border-slate-700 py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              <CardTitle className="text-base">Conversation</CardTitle>
            </div>
          </CardHeader>
          
          <CardContent className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${index}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-violet-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Vous</span>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </CardContent>

          <div className="border-t border-slate-200 dark:border-slate-700 p-4">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Posez votre question..."
                disabled={loading}
                data-testid="ai-input"
                className="flex-1"
              />
              <Button
                onClick={() => handleSend()}
                disabled={loading || !input.trim()}
                data-testid="ai-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default AIAssistantPage;
